import  React from 'react'
import * as H from 'history'
import { Alert } from 'react-bootstrap'
import { ISupportSpinner } from '../../../shared/Ajax'
import { validateFields, Label } from '../../../shared/fields'
import { NextBackButtons } from '../../applicationForm-1/controls/NextBackButtons'
import { MilestoneReportModel } from '../../../models/MilestoneReportModel'
import { MilestoneReportController } from '../../../controllers/MilestoneReportController'
import { MilestoneModel } from '../../../models/MilestoneModel'
import { Option } from '../../../shared/fields/Options'
import { MilestoneReportEditable } from '../EditableTable'
import { Columns } from '../../../shared/EditableTable/Model'
import initialDataExportEarningMilestoneReport from './ExportEarningConfig'
import { ExportEarningMilestoneReportModel } from '../../../models/ExportEarningMilestoneReportModel'
import { Backdrop } from '../../../shared/Backdrop'

export interface Props {
    history: H.History
    model: MilestoneReportModel
    markets: Option<string>[] // {value:string;label:string;}[]
    marketRegions: string[]
    preview?: boolean
    onChange: { (changes: Partial<MilestoneModel>, callback?: { (): any }): any }
}

export interface State {
    filteredMarkets: Option<string>[],
    loading: boolean
    isEarningsTableIncomplete : boolean
    isEarningsTableValid: boolean
    dynamicErrorMsg: string, // display this when `isEarningsTableValid` is false
}

const C_ERR_MSG_REGIONS_RULE_MILESTONE_REPORT = 'The regional market can only be selected if the Expenses for all regional markets are 20% or less OR at least three countries have had expenses recorded.'
// const C_ERR_MSG_NONZERO_RULE = "Earnings amount must be greater than 0."
// const C_ERR_MSG_MANDATORY_RULE = "The table is mandatory.";

export class ExportEarning extends React.Component<Props, State> implements
    ISupportSpinner {
    ajax: MilestoneReportController
    editable: MilestoneReportEditable
    refDisplayErrorTableIncomplete: HTMLDivElement
    refDisplayErrorTableInvalid: HTMLDivElement

    constructor(props: Props) {
      super(props)

      this.ajax = new MilestoneReportController()

      this.state = {
        filteredMarkets: props.markets.filter((item: Option<string>) => item.label !== 'Worldwide'),
        loading: false,
        isEarningsTableIncomplete: false, // Default to Complete (hide initial Error Msg, only show on Save+Next)
        isEarningsTableValid: true, // Default to Valid (hide initial Error Msg, only show on Save+Next)
        dynamicErrorMsg: '',
      }
    }

    /*
    Rules:
        For Tier2, Tier3 the Table is mandatory;
        For Tier 1 or RepBody, the Table is optional, i.e., it can have NO ROWS.
        However, if you add a row, then it must be completed (this applies to all Tiers/RepBody)
        0 is Allowed for both Earnings and Expenses column.
     */
    next = () => {
      const { loading } = this.state
      if (loading) {
        return
      }

      const isTblIncomplete = this.checkIsEarningsTableIncomplete()
      if (isTblIncomplete) {
        this.setState({
          isEarningsTableIncomplete: true,
        }, () => {
          this.setState({ isEarningsTableValid: true }) // hide other Error Alert
          this.refDisplayErrorTableIncomplete.focus()
        })
        return // abort Next page
      }
      // Table has rows, AND all cells filled-in (even if 0).
      this.setState({
        isEarningsTableIncomplete: false, // hide incomplete Error Alert
      })
      // validate other Business Rules: Regional% vs Total
      const isRegionRuleValid = this.checkIsRegionalRuleValid()

      if (!isRegionRuleValid) {
        this.setState({
          isEarningsTableValid: false,
          dynamicErrorMsg: C_ERR_MSG_REGIONS_RULE_MILESTONE_REPORT,
        }, () => {
          this.refDisplayErrorTableInvalid.focus()
        })
        return // abort Next page
      }
      this.setState({ isEarningsTableValid: true })

      /*
        if (!this.checkIsRegionalRuleValid()) {
            this.setState({ isEarningsTableValid: false }
                , () => {
                    this.setState({ isEarningsTableIncomplete: false })
                    this.refDisplayErrorTableInvalid.focus()
                })
            return
        }
        */

      validateFields((res) => {
        const { history, model, onChange } = this.props

        this.ajax.saveMilestoneReport(this,
          model.milestoneModel,
          (d) => {
            onChange({ ...model.milestoneModel, ...d })
            history.push(`/milestone-report/${model.milestoneModel.milestoneReportID}/reportFinalisation`)
          })
      }, this.editable)
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    getInitData(): {columns: Columns[], data: any, skipReset: boolean, model: MilestoneReportModel, modelPath: string, propertyName: string} {
      const { model } = this.props
      const tempVal = {
        ...initialDataExportEarningMilestoneReport(this.state.filteredMarkets), data: model.milestoneModel.exportEarnings, model, modelPath: 'milestoneModel.exportEarnings', propertyName: 'exportEarnings',
      }
      // console.log('initData:', tempVal);
      return tempVal
    }

    checkIsRegionalRuleValid() {
      const { model, marketRegions } = this.props

      // Get Tbl rows which are regions
      const expRowsWhichAreRegions = model.milestoneModel.exportEarnings.filter(
        (rowObj) => {
          if (marketRegions.includes(rowObj.countryCrmID)) return true
          return false
        },
      )
      if (expRowsWhichAreRegions.length == 0) return true // table is Valid, NO regions are used.

      // find CountryOnlyRows.
      const earningRowsCountryOnly = model.milestoneModel.exportEarnings.filter(
        (rowObj) => {
          if (marketRegions.includes(rowObj.countryCrmID)) return false
          return true
        },
      )

      const percentage = this.calcExpensesForRegionsAsPercentOfTotal(expRowsWhichAreRegions)
      if (percentage <= 20) {
        return true
      } if ((percentage > 20) && (earningRowsCountryOnly.length < 3)) {
        // rule is: must use AT LEAST 3 countries, if % is greater than 20
        return false
      }
      return true // if didn't fail above, it must be true.
    }

    calcExpensesForRegionsAsPercentOfTotal(earningRowsWhichAreRegions: ExportEarningMilestoneReportModel[]) {
      const { model } = this.props

      const totalExpenses = model.milestoneModel.exportEarnings.reduce(
        (cumm, rowObj) => cumm + this.toNumber(rowObj.expenses),
        0,
      )

      const regionalExpenses = earningRowsWhichAreRegions.reduce(
        (cumm, rowObj) => cumm + this.toNumber(rowObj.expenses),
        0,
      )

      return (regionalExpenses / totalExpenses) * 100
    }

    // For Calc table totals
    toNumber = (anything: any) => {
      const tempNumber = Number(anything)
      return isNaN(tempNumber) ? 0 : tempNumber
    }

    setTableIsEmptyFlagToFalse = () => {
      this.setState({
        isEarningsTableIncomplete: false,
      })
    }

    rowRemovedHandler = (index:number) => {
      console.warn('row removed:', index)
    }

    // Validate for AllRowsCompleted + Mandatory (Tier2/Tier3)
    checkIsEarningsTableIncomplete(): boolean {
      const { model } = this.props
      if (model.milestoneModel.exportEarnings.length > 0) {
        const fullyCompleteRowsArr = model.milestoneModel.exportEarnings.filter(
          (rowObj) => {
            if (Object.keys(rowObj).length === 0) {
              return false // return for Array.filter, NOT the fn
            } if (
              (!(rowObj.hasOwnProperty('countryCrmID'))
                    || (!(rowObj.hasOwnProperty('expenses'))))
                || ((rowObj.countryCrmID === undefined)
                    || (rowObj.expenses === undefined)
                    || (rowObj.earnings === undefined))
                || ((rowObj.expenses === null) || (rowObj.earnings === null))
                || (rowObj.expenses.toString() === '' || rowObj.earnings.toString() === '')
            ) {
              return false
            }
            return true // return for Array.filter, NOT the fn; ::row is non-empty
          },
        )

        if (fullyCompleteRowsArr.length === model.milestoneModel.exportEarnings.length) return false // table is good, i.e. incomplete is false;
        return true // table is bad, i.e. incomplete is true;
      } // Table is EMPTY (0 rows)
      if ((model.grantAgreementDetailModel.grantType === 'Tier 2') || (model.grantAgreementDetailModel.grantType === 'Tier 3')) {
        return true // table is bad, i.e. Mandatory for Tier2/Tier3
      }
      return false // Optional, doesn't have to be completed for non- Tier2/Tier3
    }

    /*
    checkIsEarningsTableValid() :boolean {
      const { model } = this.props

      // Get id for the Rest of the World
      let worldExportEarningRow = model.milestoneModel.exportEarnings.filter(exportEarning => {
        if (exportEarning.countryCrmID === model.worldCrmEmdgCode["15400"]) {
          return true
        }
        return false
      })

      let nonWorldExportEarningRows = model.milestoneModel.exportEarnings.filter(exportEarning => {
        if (exportEarning.countryCrmID !== model.worldCrmEmdgCode["15400"]) {
          return true
        }
        return false
      })

      if (worldExportEarningRow.length == 0) {
        return true
      }

      const percentage = this.calculateRestOfTheWorldExpensesPercentage(worldExportEarningRow)

      if (percentage < 21) {
        return true
      } else if ((percentage > 21) && (nonWorldExportEarningRows.length < 3)) {
        return false
      }

      return true
    }

    calculateRestOfTheWorldExpensesPercentage(worldExportEarningRow: ExportEarningMilestoneReportModel[]) {
      const { model } = this.props

      const total = model.milestoneModel.exportEarnings.reduce(
        (total, exportEarning) => {return total + +exportEarning.expenses},
        0
      );

      return (worldExportEarningRow[0].expenses/total) * 100;
    }
    */

    getSelectedCategories(): string[] {
      const { model: { milestoneModel: { exportEarnings } } } = this.props

      return exportEarnings.map((f) => f.countryCrmID)
    }

    render() {
      const {
        model, onChange, history, preview,
      } = this.props
      const { loading, isEarningsTableIncomplete, isEarningsTableValid } = this.state

      return (
        <>
          <h3>Export Earning</h3>
          <Alert className="emdg-page-alerts">
            <p>
              Enter your export earnings and expenditure for the reporting period. Expenditure should be attributed to the target export market.
            </p>
            <p>
              If you are exporting to more than nine markets, or, your expenses or earnings are less than 10% in a market, group these other markets together by their region.
              Note: The 'Region' option can only be used if the Expenses for 'Region' are 20% or less OR at least three countries have had expenses recorded.
            </p>
            <p>
              A 'market' refers to a country.
            </p>
            <p>
              A 'region' is a group of countries such as "North America", "Europe", "Middle East" and "Southeast Asia" etc.
            </p>
            <p>
              If you are unsure about what constitutes as a market please contact us at the EMDG Helpdesk.
            </p>
            <p>
              It is mandatory for Tier 2 or Tier 3 grantees to complete this table.
              However, if you have not received export earnings or incurred expenditure for the reporting period, please indicate this by selecting "Australia" as the country and inputting "0" into the expenses and earnings fields to be able to submit your report successfully.
            </p>
          </Alert>

          <Label label="14. Please complete the table. *" preview={preview} />
          <MilestoneReportEditable
            ref={(r) => { this.editable = r }}
            onChange={onChange}
            initData={this.getInitData()}
            uniqueSelectSelection
            selectedCategories={this.getSelectedCategories()}
            rowAddedHandler={this.setTableIsEmptyFlagToFalse}
            // rowRemovedHandler={this.rowRemovedHandler}
            maximumRows={9}
            tableName="TableExportEarning"
          />

          {isEarningsTableIncomplete && (
            <div tabIndex={-1} ref={(r) => { this.refDisplayErrorTableIncomplete = r }} className="mt-2">
              <Alert className="emdg-page-alerts emdg-page-alerts--error">
                The table has incomplete details.
              </Alert>
            </div>
          )}

          {!isEarningsTableValid && (
            <div tabIndex={-1} ref={(r) => { this.refDisplayErrorTableInvalid = r }} className="mt-2">
              <Alert className="emdg-page-alerts emdg-page-alerts--error">
                {/* The 'Rest of the World' option can only be used if the Expenses for 'Rest of the World' are 20% or less OR at least three countries have had expenses recorded. */}
                {this.state.dynamicErrorMsg}
              </Alert>
            </div>
          )}

          <NextBackButtons
            onBack={() => {
              if (confirm('Changes you made may not be saved.')) {
                history.goBack()
              }
            }}
            onNext={this.next}
          />
          <Backdrop on={loading} />
        </>
      )
    }
}
